import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { Subject, Observable, of } from 'rxjs';
import { takeUntil, debounceTime, switchMap, startWith, distinctUntilChanged, filter, map, catchError } from 'rxjs/operators';
import BusinessPartnerAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/business-partners/business-partner/business-partner.entity';
import SubRouteAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/routing/sub-route/sub-route.entity';
import SubRouteCollaboratorAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/routing/sub-route/sub-route-collaborator/sub-route-collaborator.entity';
import { BusinessPartnerFunctionAttributes } from '@albatrosdeveloper/ave-models-package';
import { StatusEnum } from '@albatrosdeveloper/ave-models-package/lib/enums';
import { BusinessPartnerTypeEnum } from 'app/shared/components/business-partner-display/business-partner-type.enum';
import { BusinessPartnerService } from 'app/modules/components/business-partners/business-partner/business-partner.service';
import { BusinessPartnerFunctionService } from 'app/modules/components/business-partners/business-partner-function/business-partner-function.service';
import { SubRouteCollaboratorService } from './sub-route-collaborator.service';
import { ICreateSubRouteCollaborator } from './interfaces/create-sub-route-collaborator.interface';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { BusinessPartnerNavigationService } from 'app/shared/services/business-partner-navigation.service';
import {
  buildQuery,
  where,
  andWhere,
  orWhere,
  andAllWhere,
  Ops,
  select,
  orderBy,
  OrderVariant,
  page,
  noop
} from '@albatrosdeveloper/ave-utils-npm/lib/utils/query.util';

/**
 * Interface extendida para colaboradores con campos adicionales para edición
 */
interface ExtendedCollaboratorAttributes extends SubRouteCollaboratorAttributes {
  isEditMode?: boolean;
  businessPartnerId?: string | null;
  businessPartnerFunctionId?: string | null;
}

/**
 * Modal para gestionar colaboradores de una sub-ruta
 *
 * Permite agregar y eliminar colaboradores (socios de negocio)
 * asignados a una sub-ruta específica mediante formularios reactivos.
 *
 * Características:
 * - Formularios reactivos con validaciones
 * - Filtrado de socios de negocio (excluye clientes)
 * - Diseño moderno con cards
 * - Confirmación de eliminación con FuseConfirmationService
 * - Compatibilidad con datos legacy (masterUserId)
 *
 * @requires BusinessPartnerService - Para cargar socios de negocio válidos
 * @requires BusinessPartnerFunctionService - Para cargar funciones de colaboradores
 * @requires SubRouteCollaboratorService - Para CRUD de colaboradores
 * @requires FuseConfirmationService - Para confirmaciones de eliminación
 *
 * @input data: SubRouteAttributes - Sub-ruta a la que se asignarán colaboradores
 *
 * @example
 * ```typescript
 * const dialogRef = this.dialog.open(AddSubRouteCollaborator, {
 *   data: subRoute,
 *   width: "800px",
 *   disableClose: true
 * });
 * dialogRef.afterClosed().subscribe(() => {
 *   // Actualizar datos después de cerrar
 * });
 * ```
 */
@Component({
  selector: 'app-add-subroute-collaborator-modal',
  styleUrl: './sub-route-collaborator.component.scss',
  templateUrl: './sub-route-collaborator.component.html',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatSelectModule,
    MatOptionModule,
    MatTooltipModule,
    MatSlideToggle,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatAutocompleteModule,
    MatInputModule
  ],
})
export class AddSubRouteCollaborator implements OnInit, OnDestroy {
  // Formulario reactivo
  collaboratorForm!: FormGroup;

  // Autocomplete controls (text inputs)
  businessPartnerSearchControl!: FormControl<string | null>;
  functionSearchControl!: FormControl<string | null>;

  // Hidden ID controls (actual values)
  businessPartnerIdControl!: FormControl<string | null>;
  functionIdControl!: FormControl<string | null>;

  // Loading states
  isLoadingFunctions = false;
  isLoadingBusinessPartners = false;
  isLoadingCollaborators = false;

  // Listas de datos (para autocomplete)
  listCollaboratorsAdd: ExtendedCollaboratorAttributes[] = [];

  // Observables para autocomplete filtrado
  filteredBusinessPartners$!: Observable<BusinessPartnerAttributes[]>;
  filteredFunctions$!: Observable<BusinessPartnerFunctionAttributes[]>;

  // Selected objects (para guardar el objeto completo)
  selectedBusinessPartner: BusinessPartnerAttributes | null = null;
  selectedFunction: BusinessPartnerFunctionAttributes | null = null;

  // Cleanup
  private destroy$ = new Subject<void>();

  // Enums expuestos para el template
  readonly StatusEnum = StatusEnum;
  readonly BusinessPartnerTypeEnum = BusinessPartnerTypeEnum;

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private subRouteCollaboratorService: SubRouteCollaboratorService,
    private readonly businessPartnerFunctionService: BusinessPartnerFunctionService,
    private readonly businessPartnerService: BusinessPartnerService,
    private readonly fuseConfirmationService: FuseConfirmationService,
    private readonly businessPartnerNavigationService: BusinessPartnerNavigationService,
    private snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<AddSubRouteCollaborator>,
    @Inject(MAT_DIALOG_DATA) public data: SubRouteAttributes
  ) {
    this.initializeForm();
  }

  private initializeForm(): void {
    // Controles de búsqueda (autocomplete text inputs)
    this.businessPartnerSearchControl = new FormControl<string | null>(null);
    this.functionSearchControl = new FormControl<string | null>(null);

    // Controles ocultos con los IDs reales (con validación)
    this.businessPartnerIdControl = new FormControl<string | null>(
      null,
      [Validators.required]
    );
    this.functionIdControl = new FormControl<string | null>(
      null,
      [Validators.required]
    );

    // Form group con los controles de ID (que son los que se validan y envían)
    this.collaboratorForm = this.fb.group({
      businessPartnerId: this.businessPartnerIdControl,
      businessPartnerFunctionId: this.functionIdControl
    });
  }

  ngOnInit(): void {
    // Validar que la sub-ruta tenga ID
    if (!this.data?._id) {
      this.snackBar.open("Error: Sub-ruta inválida", "Cerrar", { duration: 5000 });
      this.dialogRef.close();
      return;
    }

    // Inicializar autocompletes
    this.initializeAutocompletes();

    // Cargar colaboradores existentes
    this.getListCollaborators();
  }

  private initializeAutocompletes(): void {
    // Autocomplete para Business Partners (búsqueda asíncrona con debounce)
    // IMPORTANTE: También re-filtra cuando cambia la función seleccionada
    this.filteredBusinessPartners$ = this.businessPartnerSearchControl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(searchTerm => {
        if (!searchTerm || typeof searchTerm !== 'string') {
          return this.searchBusinessPartners('');
        }
        return this.searchBusinessPartners(searchTerm);
      }),
      takeUntil(this.destroy$)
    );

    // Cuando cambia la función, re-disparar búsqueda de Business Partners
    // para actualizar el filtro de combinaciones BP + Función
    this.functionIdControl.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      // Forzar re-búsqueda con el término actual
      const currentSearchTerm = this.businessPartnerSearchControl.value;
      if (typeof currentSearchTerm === 'string') {
        this.businessPartnerSearchControl.setValue(currentSearchTerm);
      }
    });

    // Autocomplete para Funciones (búsqueda asíncrona con debounce)
    this.filteredFunctions$ = this.functionSearchControl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(searchTerm => {
        if (!searchTerm || typeof searchTerm !== 'string') {
          return this.searchFunctions('');
        }
        return this.searchFunctions(searchTerm);
      }),
      takeUntil(this.destroy$)
    );
  }

  /**
   * Busca business partners filtrando por tipos excluidos con paginación.
   * Ahora delega la lógica HTTP al servicio.
   * Filtra localmente las combinaciones BP + Función ya existentes.
   *
   * Tipos EXCLUIDOS según requerimiento:
   * - BPT0001 = Cliente
   * - BPT0004 = Nuevo Cliente
   * - BPT9001 = Consumidor Final
   * - BPT9002 = Cliente Eventual
   */
  private searchBusinessPartners(searchTerm: string): Observable<BusinessPartnerAttributes[]> {
    this.isLoadingBusinessPartners = true;

    return this.businessPartnerService.searchCollaboratorCandidates(searchTerm, 10).pipe(
      map(res => {
        this.isLoadingBusinessPartners = false;
        let results = res.data || [];

        // Filtrar localmente combinaciones BP + Función duplicadas
        const selectedFunctionId = this.functionIdControl.value;

        if (selectedFunctionId) {
          results = results.filter(bp => {
            // Excluir BP que ya tienen ESA función específica asignada
            const alreadyHasThisFunction = this.listCollaboratorsAdd.some(
              collab => collab.businessPartnerId === bp._id.toString()
                     && collab.businessPartnerFunctionId === selectedFunctionId
            );
            return !alreadyHasThisFunction;
          });
        }

        return results;
      }),
      catchError(err => {
        this.isLoadingBusinessPartners = false;
        console.error('Error searching business partners:', err);
        return of([]);
      }),
      takeUntil(this.destroy$)
    );
  }

  /**
   * Busca funciones de business partners con paginación.
   * Ahora delega la lógica HTTP al servicio.
   */
  private searchFunctions(searchTerm: string): Observable<BusinessPartnerFunctionAttributes[]> {
    this.isLoadingFunctions = true;

    return this.businessPartnerFunctionService.searchActiveFunctions(searchTerm, 10).pipe(
      map(res => {
        this.isLoadingFunctions = false;
        return res.data || [];
      }),
      catchError(err => {
        this.isLoadingFunctions = false;
        console.error('Error searching functions:', err);
        return of([]);
      }),
      takeUntil(this.destroy$)
    );
  }

  /**
   * Display function para mostrar el nombre del business partner en el input
   */
  displayBusinessPartner(bp: BusinessPartnerAttributes | null): string {
    if (!bp) return '';
    const code = bp.externalKeys?.code || bp.id || '';
    return code ? `${code} - ${bp.name}` : bp.name || '';
  }

  /**
   * Display function para mostrar el nombre de la función en el input
   */
  displayFunction(func: BusinessPartnerFunctionAttributes | null): string {
    if (!func) return '';
    return func.code ? `${func.code} - ${func.name}` : func.name || '';
  }

  /**
   * Cuando selecciona un business partner del autocomplete
   */
  onBusinessPartnerSelected(bp: BusinessPartnerAttributes | null): void {
    if (bp && bp._id) {
      this.selectedBusinessPartner = bp;
      this.businessPartnerIdControl.setValue(bp._id.toString());
    } else {
      this.selectedBusinessPartner = null;
      this.businessPartnerIdControl.setValue(null);
    }
  }

  /**
   * Cuando selecciona una función del autocomplete
   */
  onFunctionSelected(func: BusinessPartnerFunctionAttributes | null): void {
    if (func && func._id) {
      this.selectedFunction = func;
      this.functionIdControl.setValue(func._id.toString());
    } else {
      this.selectedFunction = null;
      this.functionIdControl.setValue(null);
    }
  }

  /**
   * Navega al detalle del business partner en una nueva pestaña
   * IMPORTANTE: Usa stopPropagation para evitar cerrar el autocomplete
   */
  navigateToBusinessPartner(bp: BusinessPartnerAttributes, event: Event): void {
    // CRÍTICO: Evita que el click cierre el autocomplete
    event.stopPropagation();

    // Valida que el BP sea navegable
    if (!this.isBusinessPartnerNavigable(bp)) {
      return;
    }

    // Navega usando el servicio centralizado
    this.businessPartnerNavigationService.navigateToBusinessPartnerDetail(
      {
        id: bp._id?.toString(),
        code: bp.externalKeys?.code || bp.id?.toString(),
        name: bp.name,
        externalKeys: bp.externalKeys
      },
      {}, // Query params adicionales (vacío por ahora)
      true // Abrir en nueva pestaña
    );
  }

  /**
   * Verifica si un business partner es navegable
   */
  isBusinessPartnerNavigable(bp: BusinessPartnerAttributes | null): boolean {
    if (!bp) return false;

    return this.businessPartnerNavigationService.isBusinessPartnerNavigable({
      id: bp._id?.toString(),
      code: bp.externalKeys?.code || bp.id?.toString(),
      name: bp.name,
      externalKeys: bp.externalKeys
    });
  }

  /**
   * Obtiene el texto del tooltip para el botón de navegación
   */
  getNavigationTooltip(): string {
    return this.businessPartnerNavigationService.getNavigationDescription();
  }

  getListCollaborators(): void {
    this.isLoadingCollaborators = true;

    this.subRouteCollaboratorService.findBySubRouteId(this.data._id.toString())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.listCollaboratorsAdd = res?.data.map((collaborator) => ({
            ...collaborator,
            isEditMode: false,
            businessPartnerFunctionId: collaborator.businessPartnerFunction?._id?.toString() ?? null,
            businessPartnerId: collaborator.businessPartner?._id?.toString() ?? null
          }));
          this.isLoadingCollaborators = false;
        },
        error: (err) => {
          this.isLoadingCollaborators = false;
          this.snackBar.open(
            err.error.message || "Error al obtener colaboradores",
            "Cerrar",
            { duration: 5000 }
          );
        }
      });
  }

  onSaveNewCollaborator(): void {
    // Marcar todos los campos como touched para mostrar errores
    if (this.collaboratorForm.invalid) {
      this.collaboratorForm.markAllAsTouched();

      const errors: string[] = [];
      if (this.businessPartnerIdControl.hasError('required')) {
        errors.push('Socio de negocio');
      }
      if (this.functionIdControl.hasError('required')) {
        errors.push('Función');
      }

      this.snackBar.open(
        `Campos requeridos: ${errors.join(', ')}`,
        'Cerrar',
        { duration: 4000 }
      );
      return;
    }

    const formValue: ICreateSubRouteCollaborator = this.collaboratorForm.value;

    // Validar duplicados (combinación BP + Función)
    const isDuplicate = this.listCollaboratorsAdd.some(collab =>
      collab.businessPartnerId === formValue.businessPartnerId &&
      collab.businessPartnerFunctionId === formValue.businessPartnerFunctionId
    );

    if (isDuplicate) {
      this.snackBar.open(
        'Este colaborador con esta función ya está asignado',
        'Cerrar',
        { duration: 3000 }
      );
      return;
    }

    // Validar límite máximo de colaboradores (opcional)
    const MAX_COLLABORATORS = 50;
    if (this.listCollaboratorsAdd.length >= MAX_COLLABORATORS) {
      this.snackBar.open(
        `No se pueden agregar más de ${MAX_COLLABORATORS} colaboradores`,
        'Cerrar',
        { duration: 5000 }
      );
      return;
    }

    // Crear colaborador
    this.subRouteCollaboratorService.createEmbedded(this.data._id.toString(), formValue)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.snackBar.open('Colaborador creado con éxito', 'Cerrar', {
            duration: 5000
          });
          // Resetear form y autocompletes
          this.collaboratorForm.reset();
          this.businessPartnerSearchControl.reset();
          this.functionSearchControl.reset();
          this.selectedBusinessPartner = null;
          this.selectedFunction = null;
          this.getListCollaborators();
        },
        error: (err) => {
          this.snackBar.open(
            err.error.message || 'Error al crear el colaborador. Intente nuevamente.',
            'Cerrar',
            { duration: 5000 }
          );
        }
      });
  }

  updateCollaborator(result): void {
    result.isEditMode = true;
  }

  onStatusChange(event: boolean, element): void {
    const updatedStatusId = event ? StatusEnum.ACTIVE : StatusEnum.INACTIVE;
    element.statusId = updatedStatusId;
  }

  onSaveCollaboratorEdit(result): void {
    this.subRouteCollaboratorService.updateEmbedded(this.data._id.toString(), result._id.toString(), result)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.snackBar.open("Registro actualizado con éxito", "Cerrar", { duration: 5000 });
          this.getListCollaborators();
        },
        error: (err) => {
          this.snackBar.open(
            err.error.message || "Error al actualizar el registro",
            "Cerrar",
            { duration: 5000 }
          );
        }
      });
  }

  onDeleteToListCollaborator(element): void {
    const collaboratorName = element.businessPartner?.name || element.username || 'este colaborador';

    const confirmation = this.fuseConfirmationService.open({
      title: 'Eliminar Colaborador',
      message: `¿Está seguro de que desea eliminar a ${collaboratorName}? Esta acción no se puede deshacer.`,
      icon: {
        show: true,
        name: 'heroicons_outline:trash',
        color: 'warn'
      },
      actions: {
        confirm: {
          label: 'Sí, eliminar',
          color: 'warn'
        },
        cancel: {
          label: 'Cancelar'
        }
      }
    });

    confirmation.afterClosed().subscribe(result => {
      if (result === 'confirmed') {
        this.subRouteCollaboratorService.deleteEmbedded1d(this.data._id.toString(), element._id.toString())
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.snackBar.open("Colaborador eliminado con éxito", "Cerrar", { duration: 5000 });
              this.getListCollaborators();
            },
            error: (err) => {
              this.snackBar.open(
                err.error.message || "Error al eliminar el colaborador",
                "Cerrar",
                { duration: 5000 }
              );
            }
          });
      }
    });
  }


  /**
   * Abre modal para editar la función de un colaborador
   */
  openEditFunctionModal(collaborator: ExtendedCollaboratorAttributes): void {
    const dialogRef = this.dialog.open(EditCollaboratorFunctionDialogComponent, {
      width: '500px',
      disableClose: true,
      data: {
        collaborator: collaborator,
        subRouteId: this.data._id.toString(),
        allCollaborators: this.listCollaboratorsAdd
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'saved') {
        // Recargar lista de colaboradores
        this.getListCollaborators();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

/**
 * Componente de diálogo para editar la función de un colaborador
 */
@Component({
  selector: 'app-edit-collaborator-function-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatAutocompleteModule,
    MatInputModule,
    MatProgressSpinnerModule
  ],
  template: `
    <h2 mat-dialog-title class="flex items-center gap-3">
      <mat-icon class="text-accent">edit</mat-icon>
      <span>Editar Función de Colaborador</span>
    </h2>

    <mat-dialog-content class="p-6">
      <!-- Nombre del Business Partner (readonly) -->
      <div class="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
        <div class="flex items-center gap-3">
          <mat-icon class="text-blue-600">person</mat-icon>
          <div>
            <div class="font-semibold text-gray-900">
              {{ data.collaborator.businessPartner?.name || 'Sin nombre' }}
            </div>
            <div class="text-sm text-gray-600">
              {{ data.collaborator.businessPartner?.externalKeys?.code || data.collaborator.businessPartner?.id }}
            </div>
          </div>
        </div>
      </div>

      <!-- Autocomplete de Funciones -->
      <form [formGroup]="editForm" class="flex flex-col gap-4">
        <mat-form-field appearance="fill" class="w-full">
          <mat-label>Nueva Función</mat-label>
          <input
            type="text"
            matInput
            [formControl]="functionSearchControl"
            [matAutocomplete]="autoFunction"
            placeholder="Escribe para buscar..."
            autocomplete="off">
          <mat-icon matSuffix *ngIf="!isLoadingFunctions">search</mat-icon>
          <mat-spinner matSuffix *ngIf="isLoadingFunctions" diameter="20"></mat-spinner>
          <mat-autocomplete
            #autoFunction="matAutocomplete"
            [displayWith]="displayFunction.bind(this)"
            (optionSelected)="onFunctionSelected($event.option.value)">
            <mat-option *ngFor="let func of filteredFunctions$ | async" [value]="func">
              <div class="flex items-center gap-2">
                <mat-icon class="text-indigo-600">badge</mat-icon>
                <div class="flex flex-col">
                  <span class="font-semibold">{{ func.name }}</span>
                  <span class="text-xs text-gray-500">{{ func.code }}</span>
                </div>
              </div>
            </mat-option>
          </mat-autocomplete>
          <mat-hint>Función actual: {{ data.collaborator.businessPartnerFunction?.name }}</mat-hint>
          <mat-error *ngIf="functionIdControl.hasError('required')">
            La función es obligatoria
          </mat-error>
        </mat-form-field>
      </form>

      <!-- Mensaje de advertencia si hay cambios -->
      <div *ngIf="hasChanges()" class="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div class="flex items-start gap-2">
          <mat-icon class="text-yellow-600 text-sm">info</mat-icon>
          <p class="text-sm text-yellow-800">
            Estás cambiando la función de
            <strong>{{ data.collaborator.businessPartnerFunction?.name }}</strong>
            a
            <strong>{{ selectedFunction?.name }}</strong>
          </p>
        </div>
      </div>
    </mat-dialog-content>

    <mat-dialog-actions class="flex justify-end gap-3 p-4 bg-gray-50">
      <button mat-button (click)="onCancel()">
        <mat-icon class="mr-2">close</mat-icon>
        Cancelar
      </button>
      <button
        mat-raised-button
        color="primary"
        (click)="onSave()"
        [disabled]="!canSave()">
        <mat-icon class="mr-2">save</mat-icon>
        Guardar Cambios
      </button>
    </mat-dialog-actions>
  `
})
export class EditCollaboratorFunctionDialogComponent implements OnInit, OnDestroy {
  editForm!: FormGroup;
  functionSearchControl!: FormControl<string | null>;
  functionIdControl!: FormControl<string | null>;

  isLoadingFunctions = false;
  filteredFunctions$!: Observable<BusinessPartnerFunctionAttributes[]>;
  selectedFunction: BusinessPartnerFunctionAttributes | null = null;

  private destroy$ = new Subject<void>();

  // Enums expuestos para el template (si se necesita en el futuro)
  readonly StatusEnum = StatusEnum;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<EditCollaboratorFunctionDialogComponent>,
    private businessPartnerFunctionService: BusinessPartnerFunctionService,
    private subRouteCollaboratorService: SubRouteCollaboratorService,
    private fuseConfirmationService: FuseConfirmationService,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: {
      collaborator: ExtendedCollaboratorAttributes;
      subRouteId: string;
      allCollaborators: ExtendedCollaboratorAttributes[];
    }
  ) {
    this.initializeForm();
  }

  private initializeForm(): void {
    this.functionSearchControl = new FormControl<string | null>(null);
    this.functionIdControl = new FormControl<string | null>(
      this.data.collaborator.businessPartnerFunctionId,
      [Validators.required]
    );

    this.editForm = this.fb.group({
      businessPartnerFunctionId: this.functionIdControl
    });
  }

  ngOnInit(): void {
    this.initializeAutocomplete();

    // Pre-cargar la función actual en el autocomplete
    if (this.data.collaborator.businessPartnerFunction) {
      this.selectedFunction = this.data.collaborator.businessPartnerFunction;
      this.functionSearchControl.setValue(this.selectedFunction.name || '');
    }
  }

  private initializeAutocomplete(): void {
    this.filteredFunctions$ = this.functionSearchControl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(searchTerm => {
        if (!searchTerm || typeof searchTerm !== 'string') {
          return this.searchFunctions('');
        }
        return this.searchFunctions(searchTerm);
      }),
      takeUntil(this.destroy$)
    );
  }

  private searchFunctions(searchTerm: string): Observable<BusinessPartnerFunctionAttributes[]> {
    this.isLoadingFunctions = true;

    return this.businessPartnerFunctionService.searchActiveFunctions(searchTerm, 10).pipe(
      map(res => {
        this.isLoadingFunctions = false;
        return res.data || [];
      }),
      catchError(err => {
        this.isLoadingFunctions = false;
        console.error('Error searching functions:', err);
        return of([]);
      }),
      takeUntil(this.destroy$)
    );
  }

  displayFunction(func: BusinessPartnerFunctionAttributes | null): string {
    if (!func) return '';
    return func.code ? `${func.code} - ${func.name}` : func.name || '';
  }

  onFunctionSelected(func: BusinessPartnerFunctionAttributes | null): void {
    if (func && func._id) {
      this.selectedFunction = func;
      this.functionIdControl.setValue(func._id.toString());
    } else {
      this.selectedFunction = null;
      this.functionIdControl.setValue(null);
    }
  }

  hasChanges(): boolean {
    const currentFunctionId = this.data.collaborator.businessPartnerFunctionId;
    const newFunctionId = this.functionIdControl.value;
    return currentFunctionId !== newFunctionId && newFunctionId !== null;
  }

  canSave(): boolean {
    return this.editForm.valid && this.hasChanges();
  }

  onCancel(): void {
    if (this.hasChanges()) {
      const confirmation = this.fuseConfirmationService.open({
        title: 'Cambios sin guardar',
        message: '¿Está seguro de que desea cerrar sin guardar los cambios?',
        actions: {
          confirm: {
            label: 'Cerrar sin guardar',
            color: 'warn'
          },
          cancel: {
            label: 'Continuar editando'
          }
        }
      });

      confirmation.afterClosed().subscribe(result => {
        if (result === 'confirmed') {
          this.dialogRef.close(false);
        }
      });
    } else {
      this.dialogRef.close(false);
    }
  }

  onSave(): void {
    if (!this.canSave()) {
      return;
    }

    const newFunctionId = this.functionIdControl.value;
    const businessPartnerId = this.data.collaborator.businessPartnerId;

    // Validar duplicados (mismo BP + nueva función)
    const isDuplicate = this.data.allCollaborators.some(collab =>
      collab._id.toString() !== this.data.collaborator._id.toString() && // No validar contra sí mismo
      collab.businessPartnerId === businessPartnerId &&
      collab.businessPartnerFunctionId === newFunctionId
    );

    if (isDuplicate) {
      this.snackBar.open(
        'Este colaborador ya tiene esta función asignada',
        'Cerrar',
        { duration: 4000 }
      );
      return;
    }

    // Actualizar colaborador
    const updateData = {
      businessPartnerFunctionId: newFunctionId
    };

    this.subRouteCollaboratorService.updateEmbedded(
      this.data.subRouteId,
      this.data.collaborator._id.toString(),
      updateData
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.snackBar.open('Función actualizada con éxito', 'Cerrar', {
            duration: 3000
          });
          this.dialogRef.close('saved');
        },
        error: (err) => {
          this.snackBar.open(
            err.error?.message || 'Error al actualizar la función',
            'Cerrar',
            { duration: 5000 }
          );
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
